import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import type { DerivedDC } from '@/api/distributionCenters'
import type { WavePlanDcVendorTotals } from '@/api/wavePlans'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DcAllocationGrid } from './DcAllocationGrid'
import {
  DC_ROW_FILTERS,
  filterDcRows,
  groupDcRowsByVendor,
  pageDcVendors,
  vendorKey,
  vendorModelKey,
  type DcRow,
  type DcRowFilter,
} from './dc-model'

const PAGE_SIZE = 20

/** Moves focus to the other pager button (the clicked one is about to be disabled). */
const focusSibling = (button: HTMLElement, which: 'previous' | 'next') =>
  button.parentElement
    ?.querySelector<HTMLButtonElement>(
      `button[aria-label="${which === 'next' ? 'Next' : 'Previous'} SKU page"]`
    )
    ?.focus()

interface Props {
  waveId: string
  waveNumber: number
  waveLabel: string
  /** Every row of the wave (bulk edits apply to all of them). */
  rows: DcRow[]
  centers: DerivedDC[]
  /** Routing placeholders among ``centers`` (read-only). */
  virtualDcIds?: ReadonlySet<string>
  /** The wave's PO by vendor and DC, with the vendor MOA check. */
  vendorTotals?: WavePlanDcVendorTotals[]
  /** A Core plan: each line shows its DC routing. */
  core?: boolean
  readOnly: boolean
  filter: DcRowFilter
  onFilterChange: (filter: DcRowFilter) => void
  onEdit: (row: DcRow, dcId: string, value: number) => void
  onEditAll: (dcId: string, value: number) => void
  /** A vendor's or store model's DC %: applies to every line of the group in the wave. */
  onEditGroup: (
    name: string,
    lines: DcRow[],
    dcId: string,
    value: number
  ) => void
  onCopy: (row: DcRow) => void
}

/** The wave's SKU lines by vendor and store model: search, filter, paged allocation grid. */
export function DcAllocationPanel({
  waveId,
  waveNumber,
  waveLabel,
  rows,
  centers,
  virtualDcIds,
  vendorTotals,
  core,
  readOnly,
  filter,
  onFilterChange,
  onEdit,
  onEditAll,
  onEditGroup,
  onCopy,
}: Props) {
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  // Column widths outlive the per-wave grid: switching waves keeps them.
  const columnWidths = useState<Record<string, number>>({})
  // Back to the first page whenever the wave, filter or search changes.
  const [paging, setPaging] = useState({ scope: '', page: 0 })
  const scope = `${waveId}|${filter}|${search}`
  const filtered = filterDcRows(rows, search, filter)
  // A vendor or store model edit changes all its lines of the wave, whatever the filter.
  const groupLines = useMemo(
    () =>
      Object.fromEntries(
        groupDcRowsByVendor(rows).flatMap(vendor => [
          [vendorKey(vendor.key), vendor.rows],
          ...vendor.models.map(model => [
            vendorModelKey(vendor.key, model.key),
            model.rows,
          ]),
        ])
      ),
    [rows]
  )
  // Same key as the vendor groups: id, else code, else name.
  const vendorMoa = useMemo(
    () => new Map((vendorTotals ?? []).map(vendor => [vendor.key, vendor])),
    [vendorTotals]
  )
  const { sections, pageCount, page } = pageDcVendors(
    groupDcRowsByVendor(filtered),
    collapsed,
    paging.scope === scope ? paging.page : 0,
    PAGE_SIZE
  )
  const setPage = (next: number) => setPaging({ scope, page: next })
  const toggleGroup = (key: string) =>
    setCollapsed(current => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <Card className="min-w-0 gap-0 overflow-hidden py-0 shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-3 py-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">
            SKU allocation for wave {waveNumber} - {waveLabel}
          </h2>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Grouped by vendor and store model · Double-click % to edit a SKU, a
            store model or a vendor · Theoretical →{' '}
            <strong className="font-medium text-foreground">
              Adjusted units
            </strong>{' '}
            · rounded up by case pack
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <label className="relative w-full sm:w-52">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground"
            />
            <Input
              aria-label="Search SKU allocations"
              placeholder="Search SKU or vendor…"
              className="h-9 pl-8 text-xs"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </label>
          <Select
            value={filter}
            onValueChange={value => onFilterChange(value as DcRowFilter)}
          >
            <SelectTrigger
              aria-label="Filter SKU allocations"
              className="h-9 w-full text-xs sm:w-48"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DC_ROW_FILTERS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DcAllocationGrid
        key={waveId}
        columnWidths={columnWidths}
        waveRows={rows}
        sections={sections}
        groupLines={groupLines}
        collapsed={collapsed}
        onToggleGroup={toggleGroup}
        onEditGroup={onEditGroup}
        totalRows={filtered}
        centers={centers}
        virtualDcIds={virtualDcIds}
        vendorTotals={vendorMoa}
        core={core}
        waveLabel={waveLabel}
        readOnly={readOnly}
        onEdit={onEdit}
        onEditAll={onEditAll}
        onCopy={onCopy}
      />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-[11px] text-muted-foreground">
        <span role="status">
          {filtered.length} of {rows.length} lines · Totals include all filtered
          pages, excluding Parent units.
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            aria-label="Previous SKU page"
            disabled={page === 0}
            onClick={event => {
              setPage(page - 1)
              // Keep focus on the pager when this button becomes disabled.
              if (page === 1) focusSibling(event.currentTarget, 'next')
            }}
          >
            <ChevronLeft size={14} />
          </Button>
          <span aria-live="polite">
            Page {page + 1} of {pageCount}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            aria-label="Next SKU page"
            disabled={page + 1 === pageCount}
            onClick={event => {
              setPage(page + 1)
              if (page + 2 === pageCount)
                focusSibling(event.currentTarget, 'previous')
            }}
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
      <p className="border-t bg-secondary/30 px-4 py-2 text-[11px] text-muted-foreground">
        MOQ and MOA are alerts: a SKU wave below its MOQ shows Below MOQ, a
        vendor PO at a DC below the vendor's MOA shows Below MOA, and no units
        are added. Each DC quantity rounds up to a multiple of the case pack
        size shown beside the SKU. Example: 700 units with a case pack of 24 →
        720 units. Exact multiples and zero quantities stay unchanged.
      </p>
    </Card>
  )
}
